import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, {
  Column,
} from "../../components/common/data-table/DataTable";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import styles from "./Products.module.scss";
import { useAuth } from "../../context/AuthContext";
import { useCurrencies } from "../../context/CurrenciesContext";
import DeleteModal from "../../components/common/delete-modal/DeleteModal";
import { formatDate } from "../../utils/date";
import ProductsService from "../../api/services/products.service";
import ShowImage from "../../components/common/show-image/ShowImage";
import { useNavigate } from "react-router-dom";

const Products = () => {
  const { t } = useTranslation("translation");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<{
    variant_id: number | null;
    product_id: number | null;
  }>({
    product_id: null,
    variant_id: null,
  });
  const [count, setCount] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const { setButtons, searchValue } = useAuth();
  const { currencyId, currency } = useCurrencies();
  const navigate = useNavigate();

  const getData = useCallback(async () => {
    if (!searchValue) setLoading(true);
    try {
      const res = await ProductsService.getAllProducts(
        page,
        limit,
        searchValue,
        currencyId,
      );

      setProducts(res.data.data);
      setCount(res.data.count);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchValue, currencyId]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    if (count && newPage > Math.ceil(count / limit)) return;
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleDelete = async (id: number, product_id: number) => {
    try {
      await ProductsService.deleteProduct(id, product_id);
      setDeleteModal(false);
      getData();
    } catch (error) {}
  };

  useEffect(() => {
    setButtons([]);
    return () => setButtons([]);
  }, [setButtons]);

  const columns: Column[] = [
    {
      key: "sku",
      header: t("products.table.sku") as string,
    },
    {
      key: "name",
      header: t("products.table.product_name") as string,
      render: (_, row) => (
        <p className={styles.content}>
          {row.name_en || row.name_az || row.name_de || row.name_es || "-"}
        </p>
      ),
    },
    {
      key: "category",
      header: t("products.table.category") as string,
      render: (_, row) => row?.product?.category?.name || "-",
    },
    {
      key: "brand",
      header: t("products.table.brand") as string,
      render: (_, row) => row?.product?.brand?.name || "-",
    },
    {
      key: "price",
      header: t("products.table.price") as string,
      render: (p, row) => (
        <p className={styles.price_content}>
          {`${p} ${row?.currency_name || row?.product?.currency?.name || currency || ""}` ||
            "-"}
        </p>
      ),
    },
    {
      key: "discount_percent",
      header: t("products.table.discount_percent") as string,
      render: (p) => <p className={styles.content}>{`${p}%` || "-"}</p>,
    },
    {
      key: "discount_price",
      header: t("products.table.discount_price") as string,
      render: (p, row) => (
        <p className={styles.content}>
          {`${p} ${row?.currency_name || row?.product?.currency?.name || currency || ""}` ||
            "-"}
        </p>
      ),
    },
    {
      key: "stock_quantity",
      header: t("products.table.quantity") as string,
    },
    {
      key: "images",
      header: t("products.table.images") as string,
      render: (imgs) => {
        return imgs?.slice(0, 2)?.map((img: any, index: number) => {
          return img.image ? (
            <div
              key={index}
              style={{
                cursor: "pointer",
                display: "inline-block",
                marginRight: "5px",
              }}
              onClick={() => {
                setSelectedImages(imgs);
                setShowImageModal(true);
              }}
            >
              <img
                style={{ width: "40px", height: "40px", objectFit: "cover" }}
                src={img.image}
                alt={"products"}
              />
            </div>
          ) : null;
        });
      },
    },
    {
      key: "created",
      header: t("products.table.created") as string,
      render: (d) => formatDate(d),
    },
  ];

  return (
    <div className={styles.products}>
      <TitleUpdater title={t("products.title")} />
      <div>
        <DataTable
          table_title={t("products.title") as string}
          data={products}
          columns={columns}
          actions
          paginationData={{
            total: count,
            page,
            limit,
            onPageChange: handlePageChange,
            onLimitChange: handleLimitChange,
          }}
          onDetail={(row) =>
            navigate(`/dashboard/products/${row?.id}/${row?.product_id}`)
          }
          onDelete={(id, row) => {
            setDeleteModal(true);
            setDeleteId({
              variant_id: id,
              product_id: row?.product_id,
            });
          }}
          loading={loading}
        />
      </div>
      <DeleteModal
        deleteModal={deleteModal}
        onClose={() => setDeleteModal(false)}
        onSubmit={() =>
          handleDelete(
            Number(deleteId?.variant_id),
            Number(deleteId?.product_id),
          )
        }
        title={t("products.delete_title") as string}
      />
      <ShowImage
        imagesModal={showImageModal}
        setImagesModal={setShowImageModal}
        images={selectedImages}
      />
    </div>
  );
};

export default Products;
